import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { z } from 'zod';
import { Meta } from '@/components/seo/Meta';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, FieldWrapper } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/Accordion';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { TextSplit, MaskReveal } from '@/components/motion/Reveal';
import { Skeleton } from '@/components/ui/Skeleton';
import { MarkdownRenderer } from '@/components/marketing/MarkdownRenderer';
import { toast } from '@/components/ui/Toast';
import { submitContact } from '@/lib/edge-functions';
import { useFaq, useOpenRoles, useSiteSettings, useTopics } from '@/lib/cms';
import { siteSettingsDefault } from '@/lib/site-settings-default';

const BUDGETS = ['', '<$10k', '$10–50k', '$50–250k', '$250k+'] as const;
// Number of videos for the "Get videos" topic. "video series" maps to the
// monthly packages (Starter / Growth / Scale) — the founder follows up to
// confirm which one fits.
const NUMBER_OF_VIDEOS_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'video series'] as const;
// Topic names (not slugs) — the form submits `topic` as the displayed name.
const TOPIC_NEW_PROJECT = 'New project';
const TOPIC_CAREERS = 'Careers';
const TOPIC_GET_VIDEOS = 'Get videos';

const contactSchema = z.object({
  name: z.string().min(2, 'Please enter your name.'),
  email: z.string().email('Please enter a valid email.'),
  company: z.string().optional().or(z.literal('')),
  topic: z.string().min(1, 'Choose a topic.'),
  budget: z.string().optional().or(z.literal('')),
  number_of_videos: z.string().optional().or(z.literal('')),
  message: z.string().min(10, 'Tell us a bit more (≥ 10 chars).').max(1500, 'Keep it under 1500 characters.'),
  consent: z.literal(true, { errorMap: () => ({ message: 'Please agree before sending.' }) }),
});

export function ContactPage() {
  const { data: settings } = useSiteSettings();
  const { data: faq = [], isLoading: faqLoading } = useFaq();
  const { data: topics = [] } = useTopics();
  const { data: openRoles = [] } = useOpenRoles();
  const contact = settings?.contact ?? siteSettingsDefault.contact;

  const [searchParams] = useSearchParams();
  const topicSlug = searchParams.get('topic');

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const topicBySlug: Record<string, string> = {};
  for (const t of topics) {
    topicBySlug[t.slug] = t.name;
  }

  // Controlled topic so we can show/hide the right secondary field.
  // Initialise blank — `useTopics()` is async, so the slug→name lookup isn't
  // available on the first render. Resolve it in an effect once topics load.
  const [topic, setTopic] = useState<string>('');

  // Pre-select the topic from ?topic=<slug> once the topics list is hydrated.
  useEffect(() => {
    if (!topicSlug) return;
    const name = topicBySlug[topicSlug];
    if (name && name !== topic) setTopic(name);
  }, [topicSlug, topics, topic]);
  // The "Budget" form field is reused for either budget range or role
  // selection depending on the topic. Reset it whenever the topic changes so
  // a stale role title doesn't get submitted with a non-Careers topic.
  const [budget, setBudget] = useState<string>('');
  // Number-of-videos for the video service topic. Reset when topic changes.
  const [numberOfVideos, setNumberOfVideos] = useState<string>('');

  const showBudget = topic === TOPIC_NEW_PROJECT;
  const showRole = topic === TOPIC_CAREERS;
  const showNumberOfVideos = topic === TOPIC_GET_VIDEOS;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    const data = Object.fromEntries(new FormData(e.currentTarget).entries()) as Record<string, string>;
    const parsed = contactSchema.safeParse({
      ...data,
      consent: data.consent === 'on',
    });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && !next[key]) next[key] = issue.message;
      }
      // Cross-field: video service requires a Number of videos selection.
      // (We are inside `if (!parsed.success)`, so `parsed.data` is undefined;
      // we read the raw form value via the issue path or the form-data object.)
      if (topic === TOPIC_GET_VIDEOS && !data.number_of_videos) {
        next.number_of_videos = 'Pick how many videos you need.';
      }
      setErrors(next);
      return;
    }

    setSubmitting(true);
    const result = await submitContact({
      name: parsed.data.name,
      email: parsed.data.email,
      company: parsed.data.company || undefined,
      topic: parsed.data.topic,
      budget: parsed.data.budget || undefined,
      number_of_videos: parsed.data.number_of_videos || undefined,
      message: parsed.data.message,
      consent: parsed.data.consent,
    });
    setSubmitting(false);
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    toast.success("Message sent. We'll reply within 2 business days.");
    e.currentTarget.reset();
  }

  return (
    <>
      <Meta
        title="Contact"
        description="Get in touch with Vonlof — discuss your project, partnership, or career enquiry."
        path="/contact"
      />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <MaskReveal className="container-wide" duration={1}>
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Contact</p>
          </FadeIn>
          <TextSplit
            text="Talk to us"
            className="mt-3 display-serif text-display-l text-fg"
            delay={0.1}
            stagger={0.06}
          />

          <div className="mt-12 grid gap-10 lg:mt-16 lg:gap-12 lg:grid-cols-5">
            <FadeIn className="lg:col-span-3">
              <form onSubmit={onSubmit} className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <FieldWrapper id="name" label="Name" required error={errors.name}>
                    <Input
                      id="name"
                      name="name"
                      required
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </FieldWrapper>
                  <FieldWrapper id="email" label="Email" required error={errors.email}>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </FieldWrapper>
                </div>
                <FieldWrapper id="company" label="Company" hint="Optional" error={errors.company}>
                  <Input id="company" name="company" autoComplete="organization" />
                </FieldWrapper>
                <div className="grid gap-5 sm:grid-cols-2">
                  <FieldWrapper id="topic" label="Topic" required error={errors.topic}>
                    <select
                      id="topic"
                      name="topic"
                      required
                      value={topic}
                      onChange={(e) => {
                        setTopic(e.target.value);
                        setBudget('');
                        setNumberOfVideos('');
                      }}
                      className="flex h-11 w-full rounded-md border border-border bg-bg-elevated px-3 text-sm text-fg focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20"
                    >
                      <option value="">Select a topic</option>
                      {topics.map((t) => (
                        <option key={t.id} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                  </FieldWrapper>
                  {showBudget && (
                    <FieldWrapper id="budget" label="Budget" hint="Optional">
                      <select
                        id="budget"
                        name="budget"
                        value={budget}
                        onChange={(e) => setBudget(e.target.value)}
                        className="flex h-11 w-full rounded-md border border-border bg-bg-elevated px-3 text-sm text-fg focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20"
                      >
                        {BUDGETS.map((b) => (
                          <option key={b || 'none'} value={b}>
                            {b || 'Select a range'}
                          </option>
                        ))}
                      </select>
                    </FieldWrapper>
                  )}
                  {showRole && (
                    <FieldWrapper id="budget" label="Role" hint="Optional">
                      <select
                        id="budget"
                        name="budget"
                        value={budget}
                        onChange={(e) => setBudget(e.target.value)}
                        className="flex h-11 w-full rounded-md border border-border bg-bg-elevated px-3 text-sm text-fg focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20"
                      >
                        <option value="">Select a role</option>
                        {openRoles.map((r) => (
                          <option key={r.id} value={r.title}>
                            {r.title}
                          </option>
                        ))}
                      </select>
                    </FieldWrapper>
                  )}
                  {showNumberOfVideos && (
                    <FieldWrapper
                      id="number_of_videos"
                      label="Number of videos"
                      hint="1–9 single videos, or 'video series' for monthly packages."
                      error={errors.number_of_videos}
                    >
                      <select
                        id="number_of_videos"
                        name="number_of_videos"
                        required
                        value={numberOfVideos}
                        onChange={(e) => setNumberOfVideos(e.target.value)}
                        className="flex h-11 w-full rounded-md border border-border bg-bg-elevated px-3 text-sm text-fg focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20"
                      >
                        <option value="">Select an option</option>
                        {NUMBER_OF_VIDEOS_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt === 'video series' ? 'Video series (monthly package)' : opt}
                          </option>
                        ))}
                      </select>
                    </FieldWrapper>
                  )}
                </div>
                <FieldWrapper id="message" label="Message" required error={errors.message}>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    maxLength={1500}
                    rows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </FieldWrapper>
                <label className="flex items-start gap-3 rounded-md border border-border bg-bg-elevated p-4">
                  <Checkbox
                    name="consent"
                    id="consent"
                    required
                    checked={consent}
                    onCheckedChange={(v) => setConsent(v === true)}
                  />
                  <span className="text-sm text-fg-muted">
                    I agree to Vonlof contacting me about my enquiry.
                  </span>
                </label>
                {errors.consent && <p className="text-xs text-danger">{errors.consent}</p>}
                <Button
                  type="submit"
                  size="lg"
                  loading={submitting}
                  disabled={
                    !topic ||
                    !consent ||
                    name.trim().length === 0 ||
                    email.trim().length === 0 ||
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ||
                    message.trim().length === 0 ||
                    (topic === TOPIC_GET_VIDEOS && numberOfVideos === '')
                  }
                  className="w-full sm:w-auto"
                >
                  Send message
                </Button>
              </form>
            </FadeIn>

            <FadeIn delay={0.1} className="space-y-6 lg:col-span-2">
              <aside className="rounded-xl border border-border bg-bg-elevated/40 p-5 sm:p-6">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-fg-subtle">Contact</h2>
                <div className="mt-4 space-y-4">
                  <InfoLine icon={Mail} label="Email" value={contact.email} href={`mailto:${contact.email}`} />
                  <InfoLine icon={Phone} label="Phone" value={contact.phone} href={`tel:${contact.phone.replace(/\s+/g, '')}`} />
                  <InfoLine icon={MapPin} label="Address" value={contact.address_line} />
                  <InfoLine icon={Clock} label="Hours" value={contact.business_hours} />
                </div>
              </aside>
              <div className="rounded-xl border border-border bg-bg-elevated/40 p-5 text-sm text-fg-muted sm:p-6">
                We typically reply within 2 business days. For active engagements, your account manager responds within 4 hours during business hours.
              </div>
            </FadeIn>
          </div>

          {/* FAQ */}
          <section id="faq" className="mt-16 sm:mt-24">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">FAQ</p>
              <h2 className="mt-3 text-h2 text-fg">Common questions.</h2>
            </FadeIn>

            {faqLoading ? (
              <div className="mt-8 space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : faq.length === 0 ? (
              <Card className="mt-8 p-8 text-center text-sm text-fg-subtle">
                No FAQ items yet. Edit from the admin portal.
              </Card>
            ) : (
              <Accordion type="single" collapsible className="mt-8 w-full">
                {faq.map((f) => (
                  <AccordionItem key={f.id} value={f.id}>
                    <AccordionTrigger>{f.question}</AccordionTrigger>
                    <AccordionContent>
                      <MarkdownRenderer content={f.answer_md} />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </section>
        </MaskReveal>
      </main>
    </>
  );
}

function InfoLine({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
  href?: string;
}) {
  const content = (
    <span className="flex items-start gap-3">
      <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md bg-bg-subtle text-gold-500">
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex-1">
        <span className="block text-xs uppercase tracking-wider text-fg-subtle">{label}</span>
        <span className="mt-0.5 block text-sm text-fg">{value}</span>
      </span>
    </span>
  );
  return href ? (
    <a href={href} className="block transition-colors hover:text-gold-500">
      {content}
    </a>
  ) : (
    content
  );
}

// Keep Card exports for tree-shake stability
export { CardHeader, CardTitle, Stagger, StaggerItem };